import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import useEmblaCarousel from "embla-carousel-react";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Download, FileSpreadsheet, FileText, Trophy } from "lucide-react";
import { toast } from "sonner";
import { CountUp } from "@/components/CountUp";
import { PriceChangedBadge } from "@/components/PriceChangedBadge";
import { asArray, downloadUrl, formatDate } from "@/lib/api";
import { useFetch } from "@/lib/hooks";
import { cn } from "@/lib/utils";

const pad = (n) => String(n).padStart(2, "0");

function Slide({ doc, rank, active }) {
  return (
    <article data-testid={`popular-slide-${rank}`}
      className={cn("relative flex h-full flex-col overflow-hidden rounded-[2rem] border p-7 transition-[border-color,background-color,transform] duration-700 md:p-9",
        active ? "border-[#B6FF5C]/50 bg-white/[0.06]" : "border-white/10 bg-white/[0.03] md:scale-[0.96]")}>
      <span aria-hidden="true" className="ph-rank pointer-events-none absolute -right-3 -top-8 select-none font-display text-[9rem] font-extrabold leading-none md:text-[11rem]">{pad(rank)}</span>
      <div className="relative flex flex-wrap items-center gap-2">
        <Link to={`/brands/${doc.brand_slug}`} className="font-mono-ph text-xs font-semibold uppercase tracking-[0.2em] text-[#B6FF5C] hover:underline" data-testid={`popular-brand-${rank}`}>{doc.brand_name}</Link>
        {doc.price_changed && <PriceChangedBadge dark testId={`popular-price-changed-${rank}`} />}
      </div>
      <h3 className="relative mt-3 max-w-[85%] font-display text-2xl font-bold leading-tight text-white md:text-3xl">{doc.title}</h3>
      <p className="relative mt-2 text-sm text-slate-300">{doc.category}{doc.version && ` · Rev ${doc.version}`} · {formatDate(doc.updated_date)}</p>
      <div className="relative mt-auto flex flex-wrap items-end justify-between gap-5 pt-10">
        <div>
          <CountUp value={doc.download_count ?? 0} duration={2} className="block font-display text-6xl font-extrabold leading-none tracking-tight text-white md:text-7xl" testId={`popular-count-${rank}`} />
          <p className="mt-2 font-mono-ph text-[11px] uppercase tracking-[0.2em] text-[#B6FF5C]">downloads</p>
        </div>
        <div className="flex gap-2">
          {["pdf", "excel"].map((fmt) => doc[fmt] && (
            <a key={fmt} href={downloadUrl(doc.id, fmt)} download data-testid={`popular-download-${fmt}-${rank}`}
              onClick={() => toast.success(`${fmt === "pdf" ? "PDF" : "Excel"} download started`, { description: `${doc.brand_name} — ${doc.title}` })}
              className={cn("inline-flex items-center gap-1.5 rounded-full px-4 py-2.5 text-sm font-semibold transition-[background-color,transform] duration-300 hover:-translate-y-0.5",
                fmt === "pdf" ? "bg-[#B6FF5C] text-[#101625] hover:shadow-[0_0_24px_rgba(182,255,92,0.5)]" : "bg-emerald-400/15 text-emerald-200 hover:bg-emerald-400/25")}>
              {fmt === "pdf" ? <FileText className="h-4 w-4" /> : <FileSpreadsheet className="h-4 w-4" />} {fmt === "pdf" ? "PDF" : "Excel"} <Download className="h-3.5 w-3.5" />
            </a>
          ))}
        </div>
      </div>
    </article>
  );
}

export function PopularCarousel() {
  const { data, loading } = useFetch("/documents/popular?limit=10", []);
  const docs = asArray(data);
  const [emblaRef, embla] = useEmblaCarousel({ loop: docs.length > 2, align: "center" });
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  const onSelect = useCallback(() => { if (embla) setIndex(embla.selectedScrollSnap()); }, [embla]);
  useEffect(() => {
    if (!embla) return undefined;
    onSelect();
    embla.on("select", onSelect);
    embla.on("reInit", onSelect);
    return () => { embla.off("select", onSelect); embla.off("reInit", onSelect); };
  }, [embla, onSelect]);
  useEffect(() => {
    if (!embla || paused || docs.length < 2) return undefined;
    const t = setInterval(() => embla.scrollNext(), 4800);
    return () => clearInterval(t);
  }, [embla, paused, docs.length]);

  // Only shown when real downloads exist: no empty leaderboard.
  if (loading || docs.length === 0) return null;
  return (
    <section className="relative overflow-hidden bg-[#0B1020] py-20 text-white ring-1 ring-white/5 md:py-28" data-testid="popular-section"
      onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}>
      <div className="absolute -left-40 top-10 h-96 w-96 rounded-full bg-[#6366F1]/25 blur-[130px]" />
      <div className="absolute -right-20 bottom-0 h-72 w-72 rounded-full bg-[#B6FF5C]/10 blur-[110px]" />
      <div className="relative mx-auto max-w-7xl px-5 md:px-8">
        <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.15 }} transition={{ duration: 0.8 }}
          className="mb-12 flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
          <div>
            <p className="mb-4 flex items-center gap-2 font-mono-ph text-xs uppercase tracking-[0.2em] text-[#B6FF5C]"><Trophy className="h-4 w-4 text-[#B6FF5C]" /> Leaderboard</p>
            <h2 className="font-display text-3xl font-extrabold leading-[1] tracking-[-0.03em] sm:text-4xl lg:text-5xl">Most downloaded price lists</h2>
          </div>
          {docs.length > 0 && (
            <div className="flex items-center gap-5">
              <p className="font-mono-ph text-sm text-slate-300" data-testid="popular-slide-counter">
                <span className="font-display text-4xl font-extrabold text-white">{pad(index + 1)}</span> / {pad(docs.length)}
              </p>
              <div className="flex gap-2">
                <button type="button" aria-label="Previous" onClick={() => embla?.scrollPrev()} data-testid="popular-prev"
                  className="flex h-12 w-12 items-center justify-center rounded-full border border-white/20 transition-colors hover:bg-[#B6FF5C] hover:text-[#101625]"><ArrowLeft className="h-5 w-5" /></button>
                <button type="button" aria-label="Next" onClick={() => embla?.scrollNext()} data-testid="popular-next"
                  className="flex h-12 w-12 items-center justify-center rounded-full border border-white/20 transition-colors hover:bg-[#B6FF5C] hover:text-[#101625]"><ArrowRight className="h-5 w-5" /></button>
              </div>
            </div>
          )}
        </motion.div>

        {!loading && docs.length === 0 && (
          <div className="rounded-[2rem] border border-dashed border-white/20 px-6 py-16 text-center" data-testid="popular-empty">
            <p className="font-display text-2xl font-bold">The leaderboard is warming up</p>
            <p className="mx-auto mt-2 max-w-md text-sm text-slate-300">As soon as price lists are downloaded, the most popular ones will rank here with live download counts.</p>
          </div>
        )}
        {docs.length > 0 && (
          <>
            <div className="overflow-hidden" ref={emblaRef} data-lenis-prevent>
              <div className="flex touch-pan-y">
                {docs.map((d, i) => (
                  <div key={d.id} className="min-w-0 flex-[0_0_88%] pr-4 sm:flex-[0_0_70%] lg:flex-[0_0_46%]">
                    <Slide doc={d} rank={i + 1} active={i === index} />
                  </div>
                ))}
              </div>
            </div>
            <div className="mt-8 h-[3px] w-full overflow-hidden rounded-full bg-white/10">
              <motion.div className="h-full rounded-full bg-gradient-to-r from-[#7388FF] to-[#B6FF5C]" animate={{ width: `${((index + 1) / docs.length) * 100}%` }} transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }} />
            </div>
          </>
        )}
      </div>
    </section>
  );
}
